import React, { useEffect, useId, useRef, useState } from 'react';
import { CheckCircle2 } from 'lucide-react';
import { apiPost } from '../api';
import { parseApiError, ApiError } from '../apiError';
import RecoveryKeyDisplay from './RecoveryKeyDisplay';

/**
 * An address as the server shows it (maskedAddress in the payment lambda): the first
 * character, then •••, then the domain. The server masks the sign-in token's email
 * claim, trimmed and lowercased, so this reads as the `sentTo` it answers will.
 */
export const maskedAddress = (address: string): string => {
  const s = address.trim().toLowerCase();
  const at = s.lastIndexOf('@');
  if (at < 0) return '•••';
  const first = Array.from(s.slice(0, at))[0] ?? '';
  return `${first}•••${s.slice(at)}`;
};

// docs/api.md writes the messages for these three to be shown as they are. Any other
// answer gets a short message of our own, never whatever text came back.
const SHOWN_AS_SENT = new Set(['INVALID_CODE', 'CODE_COOLDOWN', 'DAILY_LIMIT']);
const GENERIC_ERROR = 'Something went wrong. Please try again.';
const CODE_RESENT = 'We sent a new code.';
// The key may have been made or replaced though no key came back, and the code may be
// spent, so "try again" would mislead. A first key has no saved key to fall back on, so
// its words send the user to the page's status instead.
const KEY_UNCONFIRMED = "We couldn't confirm your new key. If you can't recover your account with your saved key, contact support@trychaptery.com.";
const KEY_UNCONFIRMED_CREATE = "We couldn't confirm your key was made. Refresh this page to check, or contact support@trychaptery.com.";
// Shown on the code step when the dialog was opened to create a first key but the server
// says the account has one now (another tab made it, say): the code will replace it.
const HAS_KEY_NOTICE = 'This account already has a recovery key. Making a new one stops the old one working.';

const messageFor = (e: ApiError): string =>
  SHOWN_AS_SENT.has(e.code) && e.message ? e.message : GENERIC_ERROR;

const errorToShow = async (err: unknown): Promise<string> => messageFor(await parseApiError(err));

// A failed "make my key" or "make my new key" that trying again cannot fix: another
// request made or replaced the key (KEY_CHANGED), or the request may have done its work
// with no key coming back (a 5xx, a gateway error, a dropped connection, or a 200
// without a key, all of which parseApiError reads as status 0 or 5xx).
const keyUnconfirmed = (e: ApiError): boolean =>
  e.code === 'KEY_CHANGED' || e.status === 0 || e.status >= 500;

type Mode = 'create' | 'replace';

// The words for each mode. The requests are the same: since #1105 a first key is made
// behind the emailed code too, never by the signed-in session alone.
const COPY: Record<Mode, {
  opener: string;
  title: string;
  confirm: (to: string) => string;
  submit: string;
  savedTitle: string;
  savedText: string;
}> = {
  replace: {
    opener: 'Make a new key',
    title: 'Make a new recovery key',
    confirm: (to) => `Your current key will stop working as soon as the new one is made. We'll email a 6-digit code to ${to} to confirm it's you.`,
    submit: 'Make my new key',
    savedTitle: 'Save your new recovery key',
    savedText: 'Your old key no longer works. Store this one somewhere safe. We can never show it to you again.',
  },
  create: {
    opener: 'Create a recovery key',
    title: 'Create a recovery key',
    confirm: (to) => `We'll email a 6-digit code to ${to} to confirm it's you.`,
    submit: 'Make my key',
    savedTitle: 'Save your recovery key',
    savedText: 'Store this key somewhere safe. We can never show it to you again.',
  },
};

const FOCUSABLE = 'button:not([disabled]), input:not([disabled])';
const SIX_DIGITS = /^\d{6}$/;

interface Props {
  /**
   * 'create' when the account has no key yet (#1105), 'replace' (the default) when it
   * has one. Read as the flow opens: while it is open, only the server's `hasKey`
   * answer changes the words, never a new value here.
   */
  mode?: Mode;
  /** The email claim on the sign-in token, the address the server sends the code to. */
  email: string;
  /**
   * Called when the key has, or may have, changed, so the page reads its status again:
   * once the new key has been saved and the flow has closed, and when a new key could
   * not be confirmed (the flow stays open then).
   */
  onReplaced: () => void;
}

/**
 * "Make a new key", or since #1105 "Create a recovery key", on the Account page, and
 * the flow it opens (#922): confirm, enter the code mailed to the account, then save
 * the key, shown once. The key is held in this component's state only, and dropped as
 * the flow closes.
 */
const RegenerateRecoveryKey: React.FC<Props> = ({ mode = 'replace', email, onReplaced }) => {
  const [open, setOpen] = useState(false);
  // The mode the open flow is in: the prop's as it opened, then 'replace' if the server
  // answers that a key exists (hasKey) while the flow was opened to create one.
  const [flowMode, setFlowMode] = useState<Mode>(mode);
  // The server said a key exists though the flow was opened to create one.
  const [foundKey, setFoundKey] = useState(false);
  const copy = COPY[flowMode];
  // Where the code went, as the server masked it. Set once one has been sent.
  const [sentTo, setSentTo] = useState<string | null>(null);
  const [code, setCode] = useState('');
  const [newKey, setNewKey] = useState<string | null>(null);
  // Keys the key display, rather than the key itself: a React key is shown in the
  // developer tools.
  const [keysMade, setKeysMade] = useState(0);
  const [error, setError] = useState<string | null>(null);
  // Polite news, never an error: that a new code went.
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // Read synchronously, so a second click or key press cannot slip in before the
  // render that disables everything.
  const inFlight = useRef(false);
  const openerRef = useRef<HTMLButtonElement>(null);
  const dialogRef = useRef<HTMLDivElement>(null);
  const focusedStep = useRef<string | null>(null);
  const titleId = useId();
  const textId = useId();
  const codeId = useId();
  const errorId = useId();
  const foundKeyId = useId();

  const step = newKey ? 'key' : sentTo ? 'code' : 'confirm';
  const enteredCode = code.replace(/\s+/g, '');

  // Everything the flow held goes with it, the new key above all.
  const close = () => {
    setOpen(false);
    setSentTo(null);
    setCode('');
    setNewKey(null);
    setError(null);
    setNotice(null);
    openerRef.current?.focus();
  };

  // Not while a request is out: closing as the key is made would drop the only answer
  // that carries it, while the old key had already stopped working, or, for a first
  // key, while the account had a key nobody saw.
  const cancel = () => {
    if (!inFlight.current) close();
  };

  const finish = () => {
    close();
    onReplaced();
  };

  // Every request clears what the last one said, so a failed resend shows its error and
  // not the news of the one before.
  const run = async (request: () => Promise<void>, errorFor: (err: unknown) => Promise<string> = errorToShow) => {
    if (inFlight.current) return;
    inFlight.current = true;
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      await request();
    } catch (err) {
      setError(await errorFor(err));
    } finally {
      inFlight.current = false;
      setBusy(false);
    }
  };

  // `resend` is "Send a new code" on the code step; the first code is the one the
  // step's own text speaks of.
  const sendCode = (resend: boolean) => run(async () => {
    const res = await apiPost('/account/recovery-key', { action: 'regenerateCode' });
    const data = await res.body.json() as unknown as { sentTo?: string; hasKey?: boolean };
    // The server picks replace or create from the stored key, so the words follow it:
    // opened to create a first key, but one exists now, so this code replaces it.
    if (data.hasKey === true && flowMode === 'create') {
      setFlowMode('replace');
      setFoundKey(true);
    }
    setSentTo(data.sentTo || maskedAddress(email));
    // A new code replaces the one outstanding, so one typed already is no good.
    setCode('');
    if (resend) setNotice(CODE_RESENT);
  });

  // Read the status again whenever the key may have changed without a key to show, so
  // the page's "Set" date stays true.
  const makeKeyError = async (err: unknown): Promise<string> => {
    const e = await parseApiError(err);
    if (!keyUnconfirmed(e)) return messageFor(e);
    onReplaced();
    return flowMode === 'create' ? KEY_UNCONFIRMED_CREATE : KEY_UNCONFIRMED;
  };

  const makeKey = (e: React.FormEvent) => {
    e.preventDefault();
    if (!SIX_DIGITS.test(enteredCode)) return;
    run(async () => {
      const res = await apiPost('/account/recovery-key', { action: 'regenerate', code: enteredCode });
      const data = await res.body.json() as unknown as { recoveryKey?: string };
      // Thrown with no response, so it reads as status 0: unconfirmed.
      if (!data.recoveryKey) throw new Error('The answer carried no key.');
      // The code typed stays until the flow closes: nothing on this step shows it, and
      // closing is the only way off it.
      setNewKey(data.recoveryKey);
      setKeysMade(n => n + 1);
    }, makeKeyError);
  };

  // Each step starts on its first control. Focus that falls out of the dialog (the
  // button it was on was disabled during a request) comes back to it.
  useEffect(() => {
    if (!open) {
      focusedStep.current = null;
      return;
    }
    const dialog = dialogRef.current;
    if (!dialog) return;
    if (focusedStep.current !== step || !dialog.contains(document.activeElement)) {
      dialog.querySelector<HTMLElement>(FOCUSABLE)?.focus();
      focusedStep.current = step;
    }
  }, [open, step, busy]);

  const onKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    // The new key closes only by Done, once the box is ticked: it is never shown again,
    // and by then the old key, if there was one, no longer works, and after a
    // replacement another new key has to wait a day.
    if (e.key === 'Escape' && step !== 'key') cancel();
    if (e.key === 'Tab') {
      const focusable = Array.from(e.currentTarget.querySelectorAll<HTMLElement>(FOCUSABLE));
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      // The dialog itself holds focus after a click on its text; Shift+Tab from there
      // would otherwise leave it for the page behind.
      const onDialog = document.activeElement === e.currentTarget;
      if (e.shiftKey && (onDialog || document.activeElement === first)) { e.preventDefault(); last?.focus(); }
      else if (!e.shiftKey && (onDialog || document.activeElement === last)) { e.preventDefault(); first?.focus(); }
    }
  };

  const errorBanner = error && (
    <div id={errorId} className="feedback-banner feedback-error" role="alert" style={{ marginTop: '1rem' }}>{error}</div>
  );

  return (
    <>
      <button
        ref={openerRef} type="button" className="btn-secondary btn-sm"
        onClick={() => {
          setFlowMode(mode);
          setFoundKey(false);
          setOpen(true);
        }}
      >
        {COPY[mode].opener}
      </button>

      {open && (
        // The new key is kept through a click outside too, as through Escape.
        <div className="acct-modal-overlay" data-testid="recovery-key-backdrop" onClick={() => step !== 'key' && cancel()}>
          <div
            ref={dialogRef}
            className="acct-modal" role="dialog" aria-modal="true"
            aria-labelledby={titleId}
            aria-describedby={step === 'code' && foundKey ? `${textId} ${foundKeyId}` : textId}
            aria-busy={busy}
            // Focusable itself, so a click on its text keeps focus here, not on the page.
            tabIndex={-1}
            onClick={e => e.stopPropagation()}
            onKeyDown={onKeyDown}
          >
            <div className="acct-modal-head">
              <h3 id={titleId}>{step === 'key' ? copy.savedTitle : copy.title}</h3>
            </div>

            {step === 'confirm' && (
              <>
                <div id={textId} className="acct-modal-keepnote">
                  {copy.confirm(maskedAddress(email))}
                </div>
                {errorBanner}
                <div className="acct-modal-actions">
                  <button type="button" className="btn-secondary" onClick={cancel} disabled={busy}>
                    Cancel
                  </button>
                  <button type="button" className="btn-primary" onClick={() => sendCode(false)} disabled={busy}>
                    Email me a code
                  </button>
                </div>
              </>
            )}

            {step === 'code' && (
              <form onSubmit={makeKey}>
                <div id={textId} className="acct-modal-keepnote">
                  We sent a code to {sentTo}. It expires in 10 minutes.
                </div>
                {foundKey && (
                  <div id={foundKeyId} className="acct-modal-keepnote" style={{ marginTop: '0.75rem' }}>
                    {HAS_KEY_NOTICE}
                  </div>
                )}
                <div className="acct-field" style={{ marginTop: '1rem' }}>
                  <label htmlFor={codeId}>Code</label>
                  <input
                    id={codeId}
                    className="acct-input"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    value={code}
                    onChange={e => {
                      setCode(e.target.value);
                      // The news is old once the user is typing the code.
                      setNotice(null);
                    }}
                    aria-invalid={error ? true : undefined}
                    aria-describedby={error ? errorId : undefined}
                  />
                </div>
                <button type="button" className="btn-link" onClick={() => sendCode(true)} disabled={busy} style={{ marginTop: '0.75rem' }}>
                  Send a new code
                </button>
                {/* Always present, so a screen reader is listening before the news comes. */}
                <div role="status" style={{ marginTop: notice ? '0.75rem' : 0 }}>
                  {notice && <span className="acct-status-ok"><CheckCircle2 size={15} /> {notice}</span>}
                </div>
                {errorBanner}
                <div className="acct-modal-actions">
                  <button type="button" className="btn-secondary" onClick={cancel} disabled={busy}>
                    Cancel
                  </button>
                  <button type="submit" className="btn-primary" disabled={busy || !SIX_DIGITS.test(enteredCode)}>
                    {copy.submit}
                  </button>
                </div>
              </form>
            )}

            {step === 'key' && newKey && (
              <>
                <div id={textId} className="acct-modal-keepnote">
                  {copy.savedText}
                </div>
                <div style={{ marginTop: '1rem' }}>
                  <RecoveryKeyDisplay key={keysMade} recoveryKey={newKey} buttonLabel="Done" onSaved={finish} />
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default RegenerateRecoveryKey;
